import { useEffect, useRef, useState } from 'react';

import { Checkbox } from '@@/form-components/Checkbox';
import { Select } from '@@/form-components/Input';

import {
  setSnooze,
  SNOOZE_DURATION_OPTIONS_MINUTES,
  SnoozeDurationMinutes,
} from './snooze-storage';

export function SnoozeInput() {
  const [snoozeEnabled, setSnoozeEnabled] = useState(false);
  const [snoozeDuration, setSnoozeDuration] =
    useState<SnoozeDurationMinutes>(15);

  const snoozeEnabledRef = useRef(snoozeEnabled);
  const snoozeDurationRef = useRef(snoozeDuration);
  snoozeEnabledRef.current = snoozeEnabled;
  snoozeDurationRef.current = snoozeDuration;

  // when unmounting, set the snooze if it's enabled (e.g. from cancel or confirm in the confirm dialog)
  useEffect(
    () => () => {
      if (snoozeEnabledRef.current) {
        setSnooze(snoozeDurationRef.current);
      }
    },
    []
  );

  return (
    // h-[34px] to avoid layout shift when the select is shown/hidden
    <div className="mt-2 mb-4 flex h-[34px] items-center">
      <Checkbox
        bold={false}
        id="snooze-checkbox"
        checked={snoozeEnabled}
        onChange={(e) => setSnoozeEnabled(e.target.checked)}
        data-cy="snooze-checkbox"
        label="Skip change confirmation messages for"
        className="flex-none whitespace-nowrap"
      />
      {snoozeEnabled ? (
        <Select
          options={SNOOZE_DURATION_OPTIONS_MINUTES.map((m) => ({
            value: m,
            label: formatSnoozeDuration(m),
          }))}
          value={snoozeDuration}
          onChange={(e) =>
            setSnoozeDuration(Number(e.target.value) as SnoozeDurationMinutes)
          }
          data-cy="snooze-duration-select"
          className="ml-1 w-auto flex-1"
          id="snooze-duration-select"
          aria-label="Snooze duration"
        />
      ) : (
        <span className="text-muted pl-1"> (up to 8 hours)</span>
      )}
    </div>
  );
}

/** Human-readable label for a snooze duration, e.g. "30 minutes", "2 hours" */
function formatSnoozeDuration(minutes: SnoozeDurationMinutes): string {
  if (minutes < 60) return `${minutes} minutes`;
  const hours = minutes / 60;
  return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
}
